import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { saveAs } from 'file-saver';

import BaseButton from '../../../components/BaseButton';
import DropDownFilter from '../../../components/DropDownFilter';
import { ReduxStates } from '../../../redux/reduxTypes';
import { ReactComponent as DownloadIcon } from './../../../assets/icons/download-icon.svg';
import { ReactComponent as HelpIcon } from './../../../assets/icons/help-icon.svg';

import { mainStyles, cardTitleStyles, labelStyles, paragraphStyles } from '../config';
import FileUpload from './FileUpload';
import { fetchDataSets, getSampleFileFromDataSet } from '../helpers';
import MessageModal from '../../../components/MessageModal';
import { useOktaAuth } from '@okta/okta-react';

interface Props {
  setStatus: (arg1: any) => void;
}

const DataUpload = ({ setStatus }: Props): React.ReactElement => {
  const copyData = useSelector((state: ReduxStates) => state.copyData);
  const [selectedDataSet, setSelectedDataSet] = useState<any>(null);
  const [dataSets, setDataSets] = useState<any>(null);
  const [showModal, setShowModal] = useState<boolean>(false);
  const { oktaAuth } = useOktaAuth();

  const doFetchDataSets = async () => {
    try {
      const data = await fetchDataSets(oktaAuth);
      setDataSets(data);
    } catch (error) {
      console.error(error);
      setStatus({
        state: 'error',
        message: `Something went wrong fetching mappings data. Please try again.`
      });
    }
  };

  useEffect(() => {
    doFetchDataSets();
  }, []);

  useEffect(() => {
    if (dataSets) {
      setSelectedDataSet(dataSets[0]);
    }
  }, [dataSets]);

  const updateDataSet = (option: string) => {
    const selected = dataSets.filter((o: any) => o.dataset === option);
    setSelectedDataSet(selected[0]);
  };

  const downloadSampleFile = () => {
    if (selectedDataSet) {
      const file = getSampleFileFromDataSet(selectedDataSet.dataset);

      if (file) {
        saveAs(file.url, file.name);
      }
    }
  };

  const InfoComponent: React.FC = () => {
    return (
      <div className="font-nunitoLight text-sm py-[12px] px-[24px] relative z-30">
        <p className={`${cardTitleStyles} mb-2`}>Upload Your Data</p>
        <ol className="list-decimal pl-4">
          <li className={paragraphStyles}>Choose an option from the “Data Set” drop-down.</li>
          <li className={paragraphStyles}>
            The most recent data uploaded is confirmed at the bottom left.
            <ol className="list-[lower-alpha] pl-4">
              <li className={paragraphStyles}>
                To account for reinstatements in your data, you may upload data for a period previously uploaded.
              </li>
              <li className={paragraphStyles}>
                The latest file overwrites previous uploads. <b>Please note:</b> if you upload null values for a
                previously uploaded month, then that month will be overwritten without data.
              </li>
            </ol>
          </li>
          <li className={paragraphStyles}>Click "Sample File" to download a template of this data set.</li>
          <li className={paragraphStyles}>
            Open the template that was downloaded to your computer.
            <ol className="list-[lower-alpha] pl-4">
              <li className={paragraphStyles}>Add your data to this template.</li>
              <li className={paragraphStyles}>Please maintain the column headers.</li>
              <li className={paragraphStyles}>Include dates in the MM/DD/YYYY format.</li>
              <li className={paragraphStyles}>Save the file as a .CSV.</li>
            </ol>
          </li>
          <li className={paragraphStyles}>Return to the Upload Data view and click "Choose File".</li>
          <li className={paragraphStyles}>Select the file from your computer.</li>
          <li className={paragraphStyles}>
            Click Upload.
            <ol className="list-[lower-alpha] pl-4">
              <li className={paragraphStyles}>
                Unsuccessful Upload
                <ol className="list-[lower-roman] pl-4">
                  <li className={paragraphStyles}>
                    A pop-up will appear to confirm if the file upload was unsuccessful because of formatting issues.
                    <br />A file outlining these errors will be downloaded to your computer.
                  </li>
                  <li className={paragraphStyles}>
                    Review those errors, edit your file, and return to the Upload Data view to upload the corrected
                    file.
                  </li>
                </ol>
              </li>
              <li className={paragraphStyles}>
                Successful Upload
                <ol className="list-[lower-roman] pl-4">
                  <li className={paragraphStyles}>
                    A pop-up will appear to confirm that your data was uploaded successfully.
                    <br />
                    During the upload process, our system identifies the count of values that it does not recognize.
                  </li>
                  <li className={paragraphStyles}>
                    Go to the Mapping view to tell us how to understand those unrecognized values.
                  </li>
                </ol>
              </li>
            </ol>
          </li>
        </ol>
      </div>
    );
  };

  return (
    <div className="block">
      <div className={mainStyles}>
        {dataSets && selectedDataSet && (
          <React.Fragment>
            <div className="inline-block mb-8">
              <p className={cardTitleStyles}>{copyData?.data_upload_title}</p>
              <div className="inline-block pointer" role="button" onClick={() => setShowModal(true)}>
                <HelpIcon className={'inline-block'} />
              </div>
            </div>
            <div className="mb-8">
              <div className="inline-block mr-8">
                <p className={labelStyles}>{copyData?.data_upload_sets}:</p>
                <DropDownFilter
                  label={''}
                  options={dataSets.map((o: any) => o.dataset)}
                  onChange={(option: string) => updateDataSet(option)}
                  value={selectedDataSet.dataset}
                  variant={'dataUpload'}
                  automationID="slicer_dropdown_metric"
                />
              </div>
              <div className="inline-block mr-8">
                <BaseButton variant="secondary" onClick={downloadSampleFile}>
                  <DownloadIcon className={'inline-block ml-[-10px] mr-1 h-[18px]'} />
                  <span>{copyData?.data_upload_download_sample_button}</span>
                </BaseButton>
              </div>
            </div>
            <div className="mb-8">
              <FileUpload
                chooseLabel={copyData?.data_upload_choose_button}
                uploadLabel={copyData?.data_upload_button}
                dataSet={selectedDataSet}
              />
            </div>

            {selectedDataSet.latestDate && (
              <p className="font-nunitoLight text-sm">
                {copyData?.data_upload_latest} {selectedDataSet.latestDate}
              </p>
            )}
          </React.Fragment>
        )}
      </div>
      {showModal && (
        <MessageModal closeModal={() => setShowModal(false)}>
          <div>
            <InfoComponent />
          </div>
        </MessageModal>
      )}
    </div>
  );
};

export default DataUpload;
